import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "@/hooks/use-toast";
import { useActivityLogger } from "@/hooks/useActivityLogger";

const WISHLIST_KEY = (userId: string | undefined) => ["wishlist", userId];

export function useWishlist() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const { logActivity } = useActivityLogger();

  const { data: wishlist = new Set<string>(), isLoading } = useQuery({
    queryKey: WISHLIST_KEY(user?.id),
    queryFn: async () => {
      if (!user) return new Set<string>();
      const { data, error } = await supabase
        .from("course_wishlist")
        .select("course_id")
        .eq("user_id", user.id);
      if (error) throw error;
      return new Set<string>((data ?? []).map((r) => r.course_id));
    },
    enabled: Boolean(user),
    staleTime: 60_000,
  });

  const toggle = useMutation({
    mutationFn: async (courseId: string) => {
      if (!user) throw new Error("Not authenticated");
      const isSaved = wishlist.has(courseId);
      if (isSaved) {
        const { error } = await supabase
          .from("course_wishlist")
          .delete()
          .eq("user_id", user.id)
          .eq("course_id", courseId);
        if (error) throw error;
        return { courseId, saved: false };
      }
      const { error } = await supabase
        .from("course_wishlist")
        .insert({ user_id: user.id, course_id: courseId });
      if (error) throw error;
      return { courseId, saved: true };
    },
    onMutate: async (courseId: string) => {
      await qc.cancelQueries({ queryKey: WISHLIST_KEY(user?.id) });
      const prev = qc.getQueryData<Set<string>>(WISHLIST_KEY(user?.id)) ?? new Set();
      const next = new Set(prev);
      if (next.has(courseId)) next.delete(courseId);
      else next.add(courseId);
      qc.setQueryData(WISHLIST_KEY(user?.id), next);
      return { prev };
    },
    onError: (_err, _v, ctx) => {
      if (ctx?.prev) qc.setQueryData(WISHLIST_KEY(user?.id), ctx.prev);
      toast({ title: "Could not update saved list", variant: "destructive" });
    },
    onSuccess: (res) => {
      void logActivity(res.saved ? "wishlist_add" : "wishlist_remove", {
        action: res.saved ? "wishlist_add" : "wishlist_remove",
        target_type: "course",
        target_id: res.courseId,
      });
      toast({
        title: res.saved ? "Saved to your list" : "Removed from your list",
        duration: 1800,
      });
    },
  });

  return {
    wishlist,
    isLoading,
    toggle: (courseId: string) => toggle.mutate(courseId),
    isSaved: (courseId: string) => wishlist.has(courseId),
  };
}
